import { Injectable, signal } from '@angular/core';

export interface LoadingState {
  isLoading: boolean;
  message?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoadingStateService {
  private readonly states = new Map<string, ReturnType<typeof signal<LoadingState>>>();

  /**
   * Gets or creates a loading state for a specific context
   */
  getState(context: string = 'default'): ReturnType<typeof signal<LoadingState>> {
    if (!this.states.has(context)) {
      this.states.set(context, signal<LoadingState>({ isLoading: false }));
    }
    return this.states.get(context)!;
  }

  /**
   * Sets loading state for a specific context
   */
  setLoading(isLoading: boolean, context: string = 'default', message?: string): void {
    const state = this.getState(context);
    state.set({ isLoading, message, error: undefined });
  }

  /**
   * Sets error state for a specific context
   */
  setError(error: string, context: string = 'default'): void {
    const state = this.getState(context);
    state.set({ isLoading: false, error });
  }

  /**
   * Clears all states for a specific context
   */
  clear(context: string = 'default'): void {
    const state = this.getState(context);
    state.set({ isLoading: false });
  }

  /**
   * Wraps an async operation with loading state management
   */
  async withLoading<T>(
    operation: () => Promise<T>,
    context: string = 'default',
    loadingMessage?: string
  ): Promise<T> {
    try {
      this.setLoading(true, context, loadingMessage);
      const result = await operation();
      this.clear(context);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      this.setError(errorMessage, context);
      throw error;
    }
  }

  /**
   * Checks if any context is currently loading
   */
  isAnyLoading(): boolean {
    return Array.from(this.states.values()).some(state => state().isLoading);
  }

  /**
   * Gets all current loading states
   */
  getAllStates(): Record<string, LoadingState> {
    const result: Record<string, LoadingState> = {};
    for (const [key, state] of this.states) {
      result[key] = state();
    }
    return result;
  }
}